import React, { Component } from "react";
import {
  getSimulationSkinComponents,
  SIMULATION_SKIN_BUNDLES,
} from "./SimulationSkins";
import {
  CDMQB_COLOR_SCHEMES,
  CDMQB_COLOR_SCHEME_STORAGE_KEY,
  CDMQB_DEFAULT_SETTINGS,
  CDMQB_FONT_FAMILIES,
  CDMQB_FONT_SIZE_MAX,
  CDMQB_FONT_SIZE_MIN,
  CDMQB_SETTINGS_STORAGE_KEY,
  CDMQB_SKIN_ID,
  SIMULATION_SAVED_SETTING_KEYS,
  readSimulationSettingsFromSaved,
  simulationSettingsToSaved,
} from "./SimulationConstants";
import "./Simulation.css";
import logFetchError from "../../Functions/LogFetchError";
import CharacterWarningCDMQB from "./Components/CharacterPopupWarning/CharacterPopupWarningcdmqb";

// Touch selection: how long the selection has to stay unchanged before it's
// treated as finished and applied. Long enough that a brief pause while
// dragging the selection handles doesn't apply a partial selection.
const TOUCH_SELECTION_SETTLE_MS = 700;

// Touch / pen: a press this short and this still counts as a tap (not a
// long-press, scroll or selection-handle drag).
const TAP_MAX_DURATION_MS = 500;
const TAP_MAX_MOVE_PX = 10;

// Selections only count when they start inside the case/question body
// (either skin), never in the toolbar, popups, calculator, etc.
const SELECTABLE_BODY_SELECTOR =
  ".simulationBodyContainerCDMQB, .simulationBodyContainer";

// Pointer targets that must never trigger a highlight/strike: the header
// and toolbar (otherwise tapping Strikethrough applies the pending
// selection as a highlight before the mode changes), and form controls.
const IGNORED_TARGET_SELECTOR =
  "#headerContainer, input, textarea, select, button, [contenteditable='true']";

// The element for a node (text nodes -> their parent).
const toElement = (node) => {
  if (!node) return null;
  return node.nodeType === 1 ? node : node.parentElement;
};

// The text block (p / td / h3) a node belongs to: the nearest ancestor with
// a data-id that isn't a "container-break" wrapper or a highlight span.
const getParentWithDataId = (node) => {
  while (node) {
    const dataId = node.getAttribute ? node.getAttribute("data-id") : null;
    if (
      dataId !== null &&
      dataId !== "container-break" &&
      !dataId.includes("sortingOrder")
    ) {
      return node;
    }
    node = node.parentNode;
  }
  return null;
};

// Reads the saved CDMQB color scheme, falling back to "normal" when nothing
// valid is stored or storage is unavailable (e.g. private browsing).
const readStoredColorScheme = () => {
  if (typeof window === "undefined") return "normal";
  try {
    const stored = window.localStorage.getItem(CDMQB_COLOR_SCHEME_STORAGE_KEY);
    return CDMQB_COLOR_SCHEMES.some((scheme) => scheme.id === stored)
      ? stored
      : "normal";
  } catch (error) {
    return "normal";
  }
};

// Keeps only known settings with valid values, filling in defaults for
// anything missing, so a stale or hand-edited stored value can't break the
// page.
const sanitizeSettings = (raw) => {
  const settings = { ...CDMQB_DEFAULT_SETTINGS };
  if (!raw || typeof raw !== "object") return settings;

  const fontSize = Number(raw.fontSize);
  if (Number.isFinite(fontSize)) {
    settings.fontSize = Math.min(
      CDMQB_FONT_SIZE_MAX,
      Math.max(CDMQB_FONT_SIZE_MIN, Math.round(fontSize)),
    );
  }
  if (CDMQB_FONT_FAMILIES.some((font) => font.id === raw.fontFamily)) {
    settings.fontFamily = raw.fontFamily;
  }
  if (typeof raw.splitLayout === "boolean") {
    settings.splitLayout = raw.splitLayout;
  }
  if (typeof raw.confirmNextPart === "boolean") {
    settings.confirmNextPart = raw.confirmNextPart;
  }
  if (typeof raw.originalLabInstruction === "boolean") {
    settings.originalLabInstruction = raw.originalLabInstruction;
  }
  return settings;
};

// Reads the saved CDMQB settings, falling back to the defaults when nothing
// valid is stored or storage is unavailable.
const readStoredSettings = () => {
  if (typeof window === "undefined") return { ...CDMQB_DEFAULT_SETTINGS };
  try {
    const stored = window.localStorage.getItem(CDMQB_SETTINGS_STORAGE_KEY);
    return sanitizeSettings(stored ? JSON.parse(stored) : null);
  } catch (error) {
    return { ...CDMQB_DEFAULT_SETTINGS };
  }
};

// Starting settings: the account's saved values (loaded on login) win over
// this browser's localStorage copy, which in turn wins over the defaults.
// Font family isn't saved to the account, so it still comes from
// localStorage.
const initialSettings = (savedReadingOptions) =>
  sanitizeSettings({
    ...readStoredSettings(),
    ...readSimulationSettingsFromSaved(savedReadingOptions),
  });

// True when any of the account-saved settings differ between a and b.
const savedSettingsChanged = (a, b) =>
  Object.keys(SIMULATION_SAVED_SETTING_KEYS).some((key) => a[key] !== b[key]);

// Nearest ancestor of `element` that actually scrolls (overflow-y auto or
// scroll, with content taller than itself). Stops before <body> so the
// window is never treated as the scroller.
const findScrollParent = (element) => {
  let node = element ? element.parentElement : null;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

// Merges a new highlight/strike range into a list of existing ranges.
// Pure: never mutates its inputs (setState updaters can run twice under
// StrictMode, and mutating the previous state made merges unpredictable).
const mergeRanges = (existingRanges, incomingRange) => {
  const newRange = { ...incomingRange };
  const mergedRanges = [];

  existingRanges.forEach((original) => {
    const range = { ...original };
    const overlaps = newRange.start <= range.end && newRange.end >= range.start;

    if (range.mode === newRange.mode) {
      if (overlaps) {
        newRange.start = Math.min(newRange.start, range.start);
        newRange.end = Math.max(newRange.end, range.end);
      } else {
        mergedRanges.push(range);
      }
      return;
    }

    // Different mode: the new range replaces the overlapping part.
    if (!overlaps) {
      mergedRanges.push(range);
      return;
    }
    if (newRange.start > range.start && newRange.end < range.end) {
      mergedRanges.push({
        start: range.start,
        end: newRange.start,
        mode: range.mode,
      });
      mergedRanges.push({
        start: newRange.end,
        end: range.end,
        mode: range.mode,
      });
    } else if (newRange.start > range.start) {
      mergedRanges.push({ ...range, end: newRange.start });
    } else if (newRange.end < range.end) {
      mergedRanges.push({ ...range, start: newRange.end });
    }
    // Otherwise the new range fully covers it, so it's dropped.
  });

  mergedRanges.push(newRange);
  return mergedRanges;
};

export default class Simulation extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedCheckboxAmount: 0,
      displayCommentsPopup: false,
      comments: "",
      displayLabPanels: false,
      // CDMQB skin only: Instructions modal (toolbar button).
      displayInstructions: false,
      timeRemaining: props.simulationData.timeRemainingInSeconds,
      currentQuestion: props.simulationData.currentQuestion,
      submitStep: false,
      answerList: props.simulationData.answerList,
      displayAnswerPopup: false,
      displayExitCasePrompt: false,
      gradeOutput: [],
      caseEnded: false,
      showHelpPopup: false,
      // Text modification states
      textEditModes: {
        highlight: false,
        strikeThrough: false,
      },
      textModificationsById: {}, // Store text modifications per question
      // CDMQB skin only: high-contrast color scheme, persisted to
      // localStorage. Ignored by the default skin.
      colorScheme: readStoredColorScheme(),
      // CDMQB skin only: floating calculator window.
      displayCalculator: false,
      // Slide-out settings panel and its values, persisted to localStorage.
      // Both skins use text size / font; split screen, confirm before Next
      // and original lab instructions are CDMQB-only.
      displaySettings: false,
      settings: initialSettings(props.savedReadingOptions),
      displayCharacterWarning: false,
      // CDMQB skin only: Feedback popup (toolbar Feedback button).
      displayFeedback: false,
    };

    // Type of the most recent pointer ("mouse", "touch" or "pen"), and
    // whether a mouse button is currently held. Used to decide how a
    // selection is picked up (see the selection listeners below).
    this.lastPointerType = null;
    this.mouseIsDown = false;
    this.selectionTimer = null;
    this.selectionListenersAttached = false;
  }

  timer = null;

  componentDidMount() {
    if (this.timer === null && this.props.timedExam) {
      this.startCaseTimer();
    }

    // Add copy protection
    this.addCopyProtection();
  }

  componentWillUnmount() {
    this.removeCopyProtection();
    // Clean up text selection listeners
    this.detachSelectionListeners();
    clearInterval(this.timer);
  }

  // Copy protection methods
  addCopyProtection = () => {
    // Disable right-click context menu
    this.disableContextMenu = (event) => event.preventDefault();
    document.addEventListener("contextmenu", this.disableContextMenu);

    // Disable copy and cut keyboard shortcuts
    this.disableCopyCut = (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === "c" || event.key === "x")
      ) {
        event.preventDefault();
      }
    };
    document.addEventListener("keydown", this.disableCopyCut);

    // Disable the copy event
    this.disableCopyEvent = (event) => {
      event.preventDefault();
    };
    document.addEventListener("copy", this.disableCopyEvent);
  };

  removeCopyProtection = () => {
    if (this.disableContextMenu) {
      document.removeEventListener("contextmenu", this.disableContextMenu);
    }
    if (this.disableCopyCut) {
      document.removeEventListener("keydown", this.disableCopyCut);
    }
    if (this.disableCopyEvent) {
      document.removeEventListener("copy", this.disableCopyEvent);
    }
  };

  // ---------------------------------------------------------------------
  // CDMQB color scheme
  // ---------------------------------------------------------------------

  isCDMQBSkin = () => this.props.simulationSkin === CDMQB_SKIN_ID;

  setColorScheme = (colorScheme) => {
    if (CDMQB_COLOR_SCHEMES.some((scheme) => scheme.id === colorScheme)) {
      this.setState({ colorScheme });
    }
  };

  getActiveColorScheme = () =>
    CDMQB_COLOR_SCHEMES.find(
      (scheme) => scheme.id === this.state.colorScheme,
    ) || CDMQB_COLOR_SCHEMES[0];

  // CSS custom properties applied to the page root for the CDMQB skin.
  // Every property is always present: for "normal" its value is "initial"
  // instead of being omitted, because React clears a removed custom
  // property to an empty string, and an empty string is still a declared
  // value that breaks the var(--x, fallback) fallback. "initial" resets it
  // to the guaranteed-invalid value, so each fallback in the CDMQB CSS
  // (the original skin colors) takes over.
  getColorSchemeStyleVars = (activeColorScheme) => {
    const isNormal = activeColorScheme.id === "normal";
    const text = isNormal ? "initial" : activeColorScheme.text;
    const background = isNormal ? "initial" : activeColorScheme.background;

    return {
      // Page / content
      "--exam-text": text,
      "--exam-text-highlighted": background,
      "--exam-white-tone": background,
      "--exam-light-tone": background,
      "--exam-dark-tone": text,
      "--exam-separator-color": text,
      // Header / toolbar / footer / popup chrome
      "--exam-chrome-text": text,
      "--exam-chrome-bg": background,
      // Hover / active colors. The skin's yellow hover (#ffff00) disappears
      // on the yellow-background schemes, so under a scheme hover inverts
      // the two scheme colors instead (text becomes the background and
      // vice versa), which stays visible in every combination.
      "--exam-chrome-hover": background,
      "--exam-chrome-hover-bg": text,
      // Highlighted text (see getHighlightedText), inverted the same way.
      "--exam-highlight-bg": text,
      "--exam-highlight-text": background,
    };
  };

  // ---------------------------------------------------------------------
  // CDMQB calculator
  // ---------------------------------------------------------------------

  // The toolbar's Calculator button opens the calculator, or closes it if
  // it's already open.
  toggleCalculator = () => {
    this.setState((prevState) => ({
      displayCalculator: !prevState.displayCalculator,
    }));
  };

  closeCalculator = () => {
    this.setState({ displayCalculator: false });
  };

  // ---------------------------------------------------------------------
  // CDMQB instructions
  // ---------------------------------------------------------------------

  openInstructions = () => {
    this.setState({ displayInstructions: true });
  };

  closeInstructions = () => {
    this.setState({ displayInstructions: false });
  };

  // ---------------------------------------------------------------------
  // CDMQB feedback popup
  // ---------------------------------------------------------------------

  openFeedback = () => {
    this.setState({ displayFeedback: true });
  };

  closeFeedback = () => {
    this.setState({ displayFeedback: false });
  };

  // Same request as Report Bug/Feedback in Account Settings. Resolves when
  // the feedback was sent; throws otherwise so the popup can show the error.
  submitFeedback = async (feedback) => {
    const response = await fetch(`${this.props.route}/feedback.webapi`, {
      method: "POST",
      headers: {
        Token: this.props.userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify({
        CustomerId: this.props.userData.CustomerId,
        feedback,
      }),
    });

    if (response.status === 401) {
      this.props.setAuthenticationError();
      throw new Error("Authentication Error");
    }
    if (!response.ok) {
      const error = new Error("Unable to submit feedback");
      logFetchError(
        error,
        this.props.userProfile,
        this.props.userData,
        "submitFeedback",
      );
      throw error;
    }
    return response.text();
  };

  // ---------------------------------------------------------------------
  // CDMQB End of Part confirmation
  // ---------------------------------------------------------------------

  // "Yes": close the popup and submit the current part. submitStep is
  // cleared first so the popup can't stay open behind the practice-mode
  // answer popup, and a second Y keypress can't submit twice.
  confirmEndOfPart = () => {
    if (!this.state.submitStep) return;
    this.setState({ submitStep: false });
    this.submitCurrentPart();
  };

  // "No" / close / Escape: back to the question, nothing submitted.
  cancelEndOfPart = () => {
    this.setState({ submitStep: false });
  };

  // ---------------------------------------------------------------------
  // Settings panel (both skins)
  // ---------------------------------------------------------------------

  openSettings = () => {
    this.setState({ displaySettings: true });
  };

  closeSettings = () => {
    this.setState({ displaySettings: false });
  };

  updateSetting = (key, value) => {
    this.settingsTouched = true;
    this.setState((prevState) => ({
      settings: sanitizeSettings({ ...prevState.settings, [key]: value }),
    }));
  };

  // Resets the panel's settings (not the color scheme, which lives in the
  // toolbar, or the app theme, which is an account setting).
  resetSettings = () => {
    this.settingsTouched = true;
    this.setState({ settings: { ...CDMQB_DEFAULT_SETTINGS } });
  };

  // Text size and font for the CDMQB body, as custom properties on the
  // page root (read by Simulationbodycdmqb.css). The default font uses
  // "initial" for the same reason as the color scheme variables: the
  // body's font-family then falls back to what it inherits.
  getTextSettingsStyleVars = () => {
    const { fontSize, fontFamily } = this.state.settings;
    const font = CDMQB_FONT_FAMILIES.find((f) => f.id === fontFamily);
    return {
      "--cdmqb-font-size": `${fontSize}px`,
      "--cdmqb-font-family": font && font.stack ? font.stack : "initial",
    };
  };

  // Default skin: the same text size / font settings, applied to a wrapper
  // around the body (the default body CSS doesn't read the --cdmqb-*
  // variables). Font size goes on the wrapper so em/rem-relative text
  // inside scales with it; the font goes through a variable so the
  // .customFont rule in Simulation.css can override children too.
  getDefaultSkinTextSettings = () => {
    const { fontSize, fontFamily } = this.state.settings;
    const font = CDMQB_FONT_FAMILIES.find((f) => f.id === fontFamily);
    const stack = font && font.stack ? font.stack : null;
    return {
      className: `simulationBodyTextSettings${stack ? " customFont" : ""}`,
      style: {
        fontSize: `${fontSize}px`,
        ...(stack ? { "--sim-body-font-family": stack } : {}),
      },
    };
  };

  // Options for the Simulation Skin dropdown: the app's list when it's
  // passed in (same { id, skinTitle } shape as Account Settings),
  // otherwise one entry per registered skin bundle.
  getSimulationSkinOptions = () => {
    if (
      Array.isArray(this.props.simulationSkins) &&
      this.props.simulationSkins.length > 0
    ) {
      return this.props.simulationSkins;
    }
    return Object.keys(SIMULATION_SKIN_BUNDLES).map((id) => ({
      id,
      skinTitle: id === "default" ? "Default" : id,
    }));
  };

  // ---------------------------------------------------------------------
  // Next / end of part
  // ---------------------------------------------------------------------

  // Footer "Next". CDMQB: shows the End of Part confirmation, unless
  // "Confirm before Next" is off in Settings, in which case the part is
  // submitted right away. The default skin never passes through here.
  goToNextPart = () => {
    if (this.isCDMQBSkin() && !this.state.settings.confirmNextPart) {
      this.submitCurrentPart();
    } else {
      this.setState({ submitStep: true });
    }
  };

  // Submits the current part once, ignoring repeat clicks / key presses
  // while the request is still in flight.
  submitCurrentPart = async () => {
    if (this.partSubmissionInFlight) return;
    this.partSubmissionInFlight = true;
    try {
      await this.handleAnswerSubmission(this.state.currentQuestion);
    } finally {
      this.partSubmissionInFlight = false;
    }
  };

  // Brings the current part's heading into view after moving to a new part.
  // CDMQB scrolls only the element that holds the heading (the body, or the
  // question pane in split mode). scrollIntoView isn't used there because
  // it also scrolls every ancestor — including the overflow:hidden page
  // container and the window — which pushed the title bar off the top of
  // the screen on mobile. The default skin still scrolls the window.
  scrollToCurrentPart = () => {
    const answerStart = document.getElementById("beginQuestionElement");
    if (!answerStart) return;
    if (this.isCDMQBSkin()) {
      const scroller = findScrollParent(answerStart);
      if (!scroller) return;
      scroller.scrollTop +=
        answerStart.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top -
        10;
      return;
    }
    const headerContainer = document.getElementById("headerContainer");
    window.scrollTo(
      0,
      answerStart.offsetTop -
        (headerContainer ? headerContainer.offsetHeight : 0) -
        10,
    );
  };

  // ---------------------------------------------------------------------
  // Selection listeners (highlight / strikethrough)
  //
  // Mouse: the selection is applied on pointerup, as before.
  //
  // Touch / pen: touchend can't be relied on. A long-press that starts a
  // native selection often ends in touchcancel instead, dragging the
  // selection handles fires no touch events on the page at all, and a tap
  // on a toolbar button fires touchend *before* the button's click, which
  // applied the pending selection in the old mode. So on touch the
  // selection is applied once it has stopped changing for a moment
  // (selectionchange, debounced).
  // ---------------------------------------------------------------------

  attachSelectionListeners = () => {
    if (this.selectionListenersAttached) return;
    this.selectionListenersAttached = true;
    document.addEventListener("pointerdown", this.handlePointerDown, true);
    document.addEventListener("pointerup", this.handlePointerUp, true);
    document.addEventListener("selectionchange", this.handleSelectionChange);
  };

  detachSelectionListeners = () => {
    clearTimeout(this.selectionTimer);
    this.selectionTimer = null;
    if (!this.selectionListenersAttached) return;
    this.selectionListenersAttached = false;
    document.removeEventListener("pointerdown", this.handlePointerDown, true);
    document.removeEventListener("pointerup", this.handlePointerUp, true);
    document.removeEventListener("selectionchange", this.handleSelectionChange);
  };

  handlePointerDown = (event) => {
    this.lastPointerType = event.pointerType || "mouse";
    if (this.lastPointerType === "mouse") {
      this.mouseIsDown = true;
      // A mouse selection is handled on pointerup; drop any pending
      // touch-style timer so it can't fire mid-drag.
      clearTimeout(this.selectionTimer);
    } else {
      this.tapStart = {
        x: event.clientX,
        y: event.clientY,
        time: Date.now(),
      };
    }
  };

  handlePointerUp = (event) => {
    if ((event.pointerType || "mouse") !== "mouse") {
      this.handleTap(event);
      return;
    }
    this.mouseIsDown = false;
    this.handleTextSelection(event);
  };

  // Touch / pen: tapping a highlighted or struck-through piece of text
  // removes it, like clicking it with the mouse does.
  //
  // The mouse version relies on the click leaving a collapsed selection
  // (a caret) inside the text. A tap on mobile doesn't place a caret in
  // non-editable text, so instead the tapped span itself is used: every
  // modified range is rendered as its own span by getHighlightedText,
  // with its start offset in data-id.
  handleTap = (event) => {
    const start = this.tapStart;
    this.tapStart = null;
    if (!start) return;

    // Only a real tap: not a long-press (which starts a native selection),
    // a scroll, or a drag.
    if (Date.now() - start.time > TAP_MAX_DURATION_MS) return;
    if (
      Math.abs(event.clientX - start.x) > TAP_MAX_MOVE_PX ||
      Math.abs(event.clientY - start.y) > TAP_MAX_MOVE_PX
    ) {
      return;
    }

    const { highlight, strikeThrough } = this.state.textEditModes;
    if (!highlight && !strikeThrough) return;

    // A selection is in progress; let the selection handler deal with it.
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      return;
    }

    const target = toElement(event.target);
    if (
      !target ||
      target.closest(IGNORED_TARGET_SELECTOR) ||
      !target.closest(SELECTABLE_BODY_SELECTOR)
    ) {
      return;
    }

    const span = target.closest('[data-id*="-sortingOrder:"]');
    if (!span) return;
    const match = span.getAttribute("data-id").match(/-sortingOrder:(\d+)$/);
    if (!match) return;
    const parent = getParentWithDataId(span);
    if (!parent) return;

    const parentId = parent.getAttribute("data-id");
    const spanStart = parseInt(match[1], 10);
    const spanEnd = spanStart + span.textContent.length;

    // Remove the range that covers the tapped span. Plain (unmodified)
    // spans are never covered by a range, so tapping them does nothing.
    this.setState((prevState) => {
      const currentQuestionId = prevState.currentQuestion;
      const forQuestion =
        prevState.textModificationsById[currentQuestionId] || {};
      const ranges = forQuestion[parentId];
      if (!ranges) return null;

      const remaining = ranges.filter(
        (range) => !(range.start <= spanStart && range.end >= spanEnd),
      );
      if (remaining.length === ranges.length) return null;

      return {
        textModificationsById: {
          ...prevState.textModificationsById,
          [currentQuestionId]: { ...forQuestion, [parentId]: remaining },
        },
      };
    });
  };

  handleSelectionChange = () => {
    // Mouse selections are applied on pointerup instead.
    if (this.lastPointerType === "mouse") return;

    clearTimeout(this.selectionTimer);
    this.selectionTimer = setTimeout(() => {
      this.selectionTimer = null;
      if (this.lastPointerType === "mouse" || this.mouseIsDown) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        return;
      }
      const anchor = toElement(selection.anchorNode);
      if (!anchor || !anchor.closest(SELECTABLE_BODY_SELECTOR)) return;

      this.handleTextSelection(null);
    }, TOUCH_SELECTION_SETTLE_MS);
  };

  // Helper functions from original implementation
  getSelectedContainers = (range) => {
    const containers = [];
    const startContainer = range.startContainer;
    const endContainer = range.endContainer;

    if (startContainer === endContainer) {
      containers.push(startContainer);
    } else {
      // Create a tree walker to find all text nodes in the range
      const walker = document.createTreeWalker(
        range.commonAncestorContainer,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: function (node) {
            // Check if this text node intersects with our range
            const nodeRange = document.createRange();
            nodeRange.selectNodeContents(node);

            if (
              range.compareBoundaryPoints(Range.END_TO_START, nodeRange) <= 0 &&
              range.compareBoundaryPoints(Range.START_TO_END, nodeRange) >= 0
            ) {
              return NodeFilter.FILTER_ACCEPT;
            }
            return NodeFilter.FILTER_REJECT;
          },
        },
        false,
      );

      let node;
      while ((node = walker.nextNode())) {
        containers.push(node);
      }
    }

    return containers;
  };

  getDistanceToStartOfContainerBreak = (container) => {
    // If the container is a text node, check if its parent has a sortingOrder
    let parentElement = container.parentNode;

    // Walk up until we find an element with data-id containing sortingOrder
    while (parentElement && parentElement !== document) {
      if (
        parentElement.nodeType === Node.ELEMENT_NODE &&
        parentElement.getAttribute &&
        parentElement.getAttribute("data-id")
      ) {
        const dataId = parentElement.getAttribute("data-id");

        // Check if this data-id contains sortingOrder
        if (dataId.includes("-sortingOrder:")) {
          // Extract the offset from the sortingOrder
          const match = dataId.match(/-sortingOrder:(\d+)/);
          if (match) {
            return parseInt(match[1], 10);
          }
        }
      }
      parentElement = parentElement.parentNode;
    }

    // If no sortingOrder found, this is original unhighlighted text, return 0
    return 0;
  };

  handleTextSelection = (event) => {
    // Ignore the toolbar/header and form controls. Without this, tapping
    // Strikethrough on mobile applied the selection as a highlight before
    // the mode switched.
    const target = event ? toElement(event.target) : null;
    if (target && target.closest && target.closest(IGNORED_TARGET_SELECTOR)) {
      return;
    }

    const { highlight, strikeThrough } = this.state.textEditModes;
    if (!highlight && !strikeThrough) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    let removeMode = false;
    const currentMode = highlight ? "highlight" : "strikethrough";
    const selectedTextByContainer = {};

    for (let i = 0; i < selection.rangeCount; i++) {
      const range = selection.getRangeAt(i);

      // Get all text nodes in the selection
      const containers = this.getSelectedContainers(range);

      containers.forEach((container) => {
        const parentWithDataId = getParentWithDataId(container);
        if (!parentWithDataId) return;

        const text = container.textContent;

        // Get the base offset from any sortingOrder in the hierarchy
        const baseOffset = this.getDistanceToStartOfContainerBreak(container);

        let rangeStart, rangeEnd;

        if (
          container === range.startContainer &&
          container === range.endContainer
        ) {
          // Selection within a single container
          rangeStart = baseOffset + range.startOffset;
          rangeEnd = baseOffset + range.endOffset;

          if (rangeStart === rangeEnd) {
            removeMode = true;
          }
        } else if (container === range.startContainer) {
          // This is the start container
          rangeStart = baseOffset + range.startOffset;
          rangeEnd = baseOffset + text.length;
        } else if (container === range.endContainer) {
          // This is the end container
          rangeStart = baseOffset;
          rangeEnd = baseOffset + range.endOffset;
        } else {
          // This is a middle container - select all of it
          rangeStart = baseOffset;
          rangeEnd = baseOffset + text.length;
        }

        const containerId = parentWithDataId.getAttribute("data-id");
        const selectedText = text.substring(
          rangeStart - baseOffset,
          rangeEnd - baseOffset,
        );

        // Store or update the selection
        if (!selectedTextByContainer[containerId]) {
          selectedTextByContainer[containerId] = {
            text: selectedText,
            start: rangeStart,
            end: rangeEnd,
            mode: currentMode,
          };
        } else {
          // Update the end position if this container extends the selection
          selectedTextByContainer[containerId].text += selectedText;
          selectedTextByContainer[containerId].start = Math.min(
            selectedTextByContainer[containerId].start,
            rangeStart,
          );
          selectedTextByContainer[containerId].end = Math.max(
            selectedTextByContainer[containerId].end,
            rangeEnd,
          );
        }
      });
    }

    const entries = Object.entries(selectedTextByContainer);

    // Nothing inside the case/question text was selected: leave the
    // selection alone (it may be in an input or somewhere else entirely).
    if (entries.length === 0) return;

    // Copies the state it changes instead of mutating prevState.
    this.setState((prevState) => {
      const currentQuestionId = prevState.currentQuestion;
      const textModificationsForQuestion = {
        ...(prevState.textModificationsById[currentQuestionId] || {}),
      };

      if (removeMode) {
        const [parentId, { start, end }] = entries[0];
        if (textModificationsForQuestion[parentId]) {
          textModificationsForQuestion[parentId] = textModificationsForQuestion[
            parentId
          ].filter((range) => !(range.start <= start && range.end >= end));
        }
      } else {
        entries.forEach(([parentId, { start, end, mode }]) => {
          textModificationsForQuestion[parentId] = mergeRanges(
            textModificationsForQuestion[parentId] || [],
            { start, end, mode },
          );
        });
      }

      return {
        textModificationsById: {
          ...prevState.textModificationsById,
          [currentQuestionId]: textModificationsForQuestion,
        },
      };
    });

    selection.removeAllRanges();
  };

  // Helper function to apply highlighting for all highlighted texts.
  //
  // Keys are derived from the range positions, so they stay the same from
  // one render to the next. (They used to come from an ever-increasing
  // counter, which made React replace every span on every render — the
  // timer re-renders once a second — destroying the DOM nodes the user's
  // in-progress selection was attached to. On mobile, where selecting
  // takes a few seconds, that's why highlighting only worked once per
  // paragraph.)
  getHighlightedText = (text, parentId) => {
    if (typeof text === "number") {
      text = String(text);
    }
    // Retrieve highlighted ranges for the specific parentId under the currentQuestion
    const currentQuestionId = this.state.currentQuestion;
    const highlightedRanges = [
      ...(this.state.textModificationsById[currentQuestionId]?.[parentId] ||
        []),
    ];

    // If no highlighted ranges for this element, return original text
    if (highlightedRanges.length === 0) return text;

    // CDMQB skin: highlight colors come from the color scheme. With
    // "Normal" it's the usual yellow with black text; under a scheme it
    // inverts the scheme colors, so a highlight never disappears into a
    // yellow page ("Black on Yellow") or turns unreadable ("Yellow on
    // Black").
    const highlightStyle = this.isCDMQBSkin()
      ? {
          backgroundColor: "var(--exam-highlight-bg, yellow)",
          color: "var(--exam-highlight-text, #000)",
        }
      : { backgroundColor: "yellow" };

    // Create an array to hold parts of the text with highlighting
    const parts = [];
    let lastIndex = 0;
    const sortedRanges = highlightedRanges.sort((a, b) => a.start - b.start);

    // Iterate through the highlighted ranges
    sortedRanges.forEach(({ start, end, mode }) => {
      // Add text before the current highlight
      if (lastIndex < start) {
        parts.push(
          <span
            data-id={`${parentId}-sortingOrder:${lastIndex}`}
            key={`${parentId}-plain-${lastIndex}-${start}`}
          >
            {text.slice(lastIndex, start)}
          </span>,
        );
      }

      // Add the highlighted part of the text
      parts.push(
        <span
          data-id={`${parentId}-sortingOrder:${start}`}
          key={`${parentId}-mod-${start}-${end}-${mode}`}
          style={
            mode === "highlight"
              ? highlightStyle
              : { textDecoration: "line-through" }
          }
        >
          {text.slice(start, end)}
        </span>,
      );

      // Update the last index
      lastIndex = Math.max(lastIndex, end);
    });

    // Add any remaining text after the last highlight
    if (lastIndex < text.length) {
      parts.push(
        <span
          data-id={`${parentId}-sortingOrder:${lastIndex}`}
          key={`${parentId}-plain-${lastIndex}-end`}
        >
          {text.slice(lastIndex)}
        </span>,
      );
    }

    // Return a single element that represents the entire text with highlights
    return <>{parts}</>;
  };

  componentDidUpdate(prevProps, prevState) {
    // Listen for selections only while Highlight or Strikethrough is on.
    if (
      prevState.textEditModes.highlight !==
        this.state.textEditModes.highlight ||
      prevState.textEditModes.strikeThrough !==
        this.state.textEditModes.strikeThrough
    ) {
      if (
        this.state.textEditModes.highlight ||
        this.state.textEditModes.strikeThrough
      ) {
        this.attachSelectionListeners();
      } else {
        this.detachSelectionListeners();
      }
    }

    // Persist the CDMQB color scheme choice.
    if (prevState.colorScheme !== this.state.colorScheme) {
      try {
        window.localStorage.setItem(
          CDMQB_COLOR_SCHEME_STORAGE_KEY,
          this.state.colorScheme,
        );
      } catch (error) {
        // Ignore write failures (e.g. private browsing); the selection just
        // won't persist across reloads.
      }
    }

    // Saved options arrived after the case started (slow login fetch):
    // apply them, unless the user has already changed something here.
    if (
      !prevProps.savedReadingOptions &&
      this.props.savedReadingOptions &&
      !this.settingsTouched
    ) {
      this.applyingSavedSettings = true;
      this.setState({
        settings: initialSettings(this.props.savedReadingOptions),
      });
    }

    // Persist the settings: localStorage for this browser, and the
    // account (via the reading options) so they come back on next login.
    if (prevState.settings !== this.state.settings) {
      try {
        window.localStorage.setItem(
          CDMQB_SETTINGS_STORAGE_KEY,
          JSON.stringify(this.state.settings),
        );
      } catch (error) {
        // Ignore write failures; settings just won't persist.
      }

      if (this.applyingSavedSettings) {
        // Just loaded from the account — nothing new to save.
        this.applyingSavedSettings = false;
      } else if (
        this.props.saveReadingOptions &&
        savedSettingsChanged(prevState.settings, this.state.settings)
      ) {
        this.props.saveReadingOptions(
          simulationSettingsToSaved(this.state.settings),
        );
      }
    }
  }

  // Toggle text edit modes
  toggleHighlight = () => {
    this.setState((prevState) => ({
      textEditModes: {
        highlight: !prevState.textEditModes.highlight,
        strikeThrough: false,
      },
    }));
  };

  toggleStrikeThrough = () => {
    this.setState((prevState) => ({
      textEditModes: {
        highlight: false,
        strikeThrough: !prevState.textEditModes.strikeThrough,
      },
    }));
  };

  startCaseTimer = () => {
    this.timer = setInterval(() => {
      let timeRemaining = this.state.timeRemaining;
      if (timeRemaining - 1 === 0) {
        this.setState({ caseEnded: true, timeRemaining: 0 });
        clearInterval(this.timer);
        this.handleAnswerSubmission(
          this.props.simulationData.answerList.length,
        );
      } else {
        timeRemaining--;
        this.setState({ timeRemaining });
      }
    }, 1000);
  };

  pauseCaseTimer = () => {
    clearInterval(this.timer);
  };

  updateCheckboxAnswer = (optionName) => {
    let index = 0;
    let currentQuestion = this.state.currentQuestion - 1;
    let answerList = [...this.state.answerList];
    for (let i = 0; i < answerList[currentQuestion].length; ++i) {
      if (optionName === answerList[currentQuestion][i].option) {
        index = i;
        break;
      }
    }
    answerList[currentQuestion][index].selected =
      !answerList[currentQuestion][index].selected;
    let selectedCheckboxAmount = this.state.selectedCheckboxAmount;
    if (answerList[currentQuestion][index].selected) {
      selectedCheckboxAmount++;
    } else {
      selectedCheckboxAmount--;
    }
    this.setState({ answerList, selectedCheckboxAmount });
  };

  modifyInputAnswer = (e, index) => {
    let answerInfo = [...this.state.answerList];
    if (e.target.value.length <= 100) {
      answerInfo[this.state.currentQuestion - 1][index] = e.target.value;
      this.setState({ answerInfo });
    } else {
      this.setState({ displayCharacterWarning: true });
    }
  };

  formatAnswer = (currentQuestion) => {
    let formattedAnswerList = [];
    this.state.answerList.forEach((value, index) => {
      if (index < currentQuestion) {
        let answer = {
          customerId: this.props.userData.CustomerId,
          caseId: this.props.selectedCaseId,
          questionId: this.props.simulationData.qo[index].QUESTIONID,
          caseguid: this.props.caseguid,
          customerAnswersBlank: null,
          customerAnswerMC: null,
          timeLeftInSec: this.state.timeRemaining,
          completeSubmission:
            index + 1 === this.props.simulationData.answerList.length
              ? true
              : false,
          isTimed: this.props.timedExam,
        };
        if (typeof value[0] === "string") {
          answer.customerAnswersBlank = [...value];
        } else {
          let multipleChoiceOptions = [];
          this.props.simulationData.ao.forEach((aoValue, aoIndex) => {
            if (aoValue.QUESTIONID === answer.questionId) {
              multipleChoiceOptions.push(aoValue);
            }
          });
          answer.customerAnswerMC = [];
          value.forEach((value, index) => {
            if (value.selected) {
              answer.customerAnswerMC.push(
                multipleChoiceOptions[index].ANSWERID,
              );
            }
          });
        }
        formattedAnswerList.push(answer);
      }
    });

    if (currentQuestion === this.props.simulationData.answerList.length) {
      return formattedAnswerList;
    } else {
      return formattedAnswerList[formattedAnswerList.length - 1];
    }
  };

  handleAnswerSubmission = async (currentQuestion) => {
    if (currentQuestion === this.props.simulationData.answerList.length) {
      await this.fetchFinalQuestionSubmission(currentQuestion);
    } else {
      if (this.props.practiceMode) {
        await this.fetchPracticeModeSubmission(currentQuestion);
      } else {
        await this.fetchMoveOn(currentQuestion);
      }
    }
  };

  fetchPracticeModeSubmission = async (currentQuestion) => {
    this.props.setFetchOutstanding(true);
    let answer = this.formatAnswer(currentQuestion);
    await fetch(`${this.props.route}/moveon.webapi`, {
      method: "POST",
      headers: {
        Token: this.props.userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(answer),
    })
      .then((res) => {
        if (res.ok) {
          return res.text();
        } else if (res.status === 401) {
          this.props.setAuthenticationError();
        } else {
          throw new Error("Unable to submit answers");
        }
      })
      .then(async () => {
        await fetch(`${this.props.route}/grading.webapi`, {
          method: "POST",
          headers: {
            Token: this.props.userData.Token,
            "Content-Type": "text/plain",
          },
          body: JSON.stringify([answer]),
        })
          .then((res) => {
            if (res.ok) {
              return res.text();
            } else if (res.status === 401) {
              this.props.setAuthenticationError();
            } else {
              throw new Error("Unable to submit answers");
            }
          })
          .then((res) => {
            this.props.setFetchOutstanding(false);
            let result = JSON.parse(res);
            this.pauseCaseTimer();
            this.setState({
              displayAnswerPopup: true,
              gradeOutput: result.pgolist,
              selectedCheckboxAmount: 0,
            });
          });
      })
      .catch((error) => {
        this.props.setFetchOutstanding(false);
        console.log(error);
        logFetchError(
          error,
          this.props.userProfile,
          this.props.userData,
          "fetchPracticeModeSubmission",
        );
      });
  };

  fetchMoveOn = async (currentQuestion) => {
    this.props.setFetchOutstanding(true);
    let answer = this.formatAnswer(currentQuestion);
    await fetch(`${this.props.route}/moveon.webapi`, {
      method: "POST",
      headers: {
        Token: this.props.userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(answer),
    })
      .then((res) => {
        if (res.ok) {
          return res.text();
        } else if (res.status === 401) {
          this.props.setAuthenticationError();
        } else {
          throw new Error("Unable to submit answers");
        }
      })
      .then(() => {
        this.props.setFetchOutstanding(false);
        this.setState(
          {
            currentQuestion: currentQuestion + 1,
            submitStep: false,
            selectedCheckboxAmount: 0,
          },
          this.scrollToCurrentPart,
        );
      })
      .catch((error) => {
        this.props.setFetchOutstanding(false);
        console.log(error);
        logFetchError(
          error,
          this.props.userProfile,
          this.props.userData,
          "fetchMoveOn",
        );
      });
  };

  fetchFinalQuestionSubmission = async (currentQuestion) => {
    this.props.setFetchOutstanding(true);
    let answers = this.formatAnswer(currentQuestion);

    await fetch(`${this.props.route}/grading.webapi`, {
      method: "POST",
      headers: {
        Token: this.props.userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(answers),
    })
      .then((res) => {
        if (res.ok) {
          return res.text();
        } else if (res.status === 401) {
          this.props.setAuthenticationError();
        } else {
          throw new Error("Unable to submit answers");
        }
      })
      .then(async (res) => {
        this.props.setFetchOutstanding(false);
        let gradingData = JSON.parse(res);
        this.props.displayGrading(gradingData);
        if (gradingData && this.props.triggerGamificationEvent) {
          await this.props.triggerGamificationEvent(
            [gradingData.caseId ?? this.props.caseData.ID],
            gradingData.TotalGrade,
          );
        }
      })
      .catch((error) => {
        this.props.setFetchOutstanding(false);
        console.log(error);
        logFetchError(
          error,
          this.props.userProfile,
          this.props.userData,
          "fetchFinalQuestionSubmission",
        );
      });
  };

  continueCaseHandler = () => {
    if (
      this.state.currentQuestion ===
        this.props.simulationData.answerList.length ||
      this.state.caseEnded
    ) {
      this.props.displayGrading();
    } else {
      this.startCaseTimer();
      this.setState(
        {
          currentQuestion: this.state.currentQuestion + 1,
          submitStep: false,
          displayAnswerPopup: false,
        },
        this.scrollToCurrentPart,
      );
    }
  };

  render() {
    const {
      Header: SimulationHeaderSkin,
      Body: SimulationBodySkin,
      Footer: SimulationFooterSkin,
      LabPanels: LabPanelsSkin,
      Comments: CommentsSkin,
      AnswerPopup: AnswerPopupSkin,
      ExitCasePopup: ExitCasePopupSkin,
      HelpPopup: HelpPopupSkin,
      Calculator: CalculatorSkin,
      Instructions: InstructionsSkin,
      EndOfPartPopup: EndOfPartPopupSkin,
      SettingsPanel: SettingsPanelSkin,
      FeedbackPopup: FeedbackPopupSkin,
    } = getSimulationSkinComponents(this.props.simulationSkin);

    // Color scheme applies to the CDMQB skin only.
    const isCDMQBSkin = this.isCDMQBSkin();
    const activeColorScheme = this.getActiveColorScheme();
    const colorSchemeProps = isCDMQBSkin
      ? {
          colorScheme: activeColorScheme.id,
          setColorScheme: this.setColorScheme,
          activeColorScheme,
          colorSchemes: CDMQB_COLOR_SCHEMES,
        }
      : {};

    const defaultTextSettings = isCDMQBSkin
      ? null
      : this.getDefaultSkinTextSettings();
    const simulationBody = (
      <SimulationBodySkin
        updateCheckboxAnswer={this.updateCheckboxAnswer}
        selectedCheckboxAmount={this.state.selectedCheckboxAmount}
        simulationData={this.props.simulationData}
        currentQuestion={this.state.currentQuestion}
        modifyInputAnswer={this.modifyInputAnswer}
        answerList={this.state.answerList}
        caseNumber={this.props.caseData.ID}
        showHelpPopup={() => this.setState({ showHelpPopup: true })}
        getHighlightedText={this.getHighlightedText}
        splitLayout={isCDMQBSkin ? this.state.settings.splitLayout : undefined}
        settings={this.state.settings}
        displayLabPanels={() => this.setState({ displayLabPanels: true })}
        displayInstructions={this.openInstructions}
      />
    );

    return (
      <div
        className={`simulationPageContainer${
          isCDMQBSkin ? " simulationPageContainerCDMQB" : ""
        }${
          isCDMQBSkin && activeColorScheme.id !== "normal"
            ? " colorSchemeActiveCDMQB"
            : ""
        }`}
        style={
          isCDMQBSkin
            ? {
                ...this.getColorSchemeStyleVars(activeColorScheme),
                ...this.getTextSettingsStyleVars(),
              }
            : undefined
        }
      >
        <SimulationHeaderSkin
          timedExam={this.props.timedExam}
          timeRemaining={this.state.timeRemaining}
          simulationData={this.props.simulationData}
          currentQuestion={this.state.currentQuestion}
          displayLabPanels={() => this.setState({ displayLabPanels: true })}
          displayInstructions={this.openInstructions}
          timerSpeed={this.props.timerSpeed}
          textEditModes={this.state.textEditModes}
          toggleHighlight={this.toggleHighlight}
          toggleStrikeThrough={this.toggleStrikeThrough}
          displayCalculator={this.toggleCalculator}
          calculatorOpen={this.state.displayCalculator}
          openSettings={this.openSettings}
          settingsOpen={this.state.displaySettings}
          settings={this.state.settings}
          showFeedback={this.openFeedback}
          feedbackOpen={this.state.displayFeedback}
          {...colorSchemeProps}
        />
        {isCDMQBSkin ? (
          simulationBody
        ) : (
          // Default skin: wrapper carrying the Settings text size / font.
          // (CDMQB's flex layout needs the body as a direct child and reads
          // its settings from page-root variables instead.)
          <div
            className={defaultTextSettings.className}
            style={defaultTextSettings.style}
          >
            {simulationBody}
          </div>
        )}
        <SimulationFooterSkin
          showFeedback={this.openFeedback}
          feedbackOpen={this.state.displayFeedback}
          showComments={() => this.setState({ displayCommentsPopup: true })}
          submitStep={this.state.submitStep}
          updateSubmitStep={() => this.setState({ submitStep: true })}
          goToNextPart={this.goToNextPart}
          submitAnswer={() =>
            this.handleAnswerSubmission(this.state.currentQuestion)
          }
          displayExitCasePrompt={() =>
            this.setState({ displayExitCasePrompt: true })
          }
          displayLabPanels={() => this.setState({ displayLabPanels: true })}
        />
        <LabPanelsSkin
          displayLabPanels={this.state.displayLabPanels}
          closeLabPanels={() => this.setState({ displayLabPanels: false })}
        />
        {InstructionsSkin && (
          <InstructionsSkin
            displayInstructions={this.state.displayInstructions}
            closeInstructions={this.closeInstructions}
          />
        )}
        <CommentsSkin
          displayCommentsPopup={this.state.displayCommentsPopup}
          comments={this.state.comments}
          updateComments={(event) =>
            this.setState({ comments: event.target.value })
          }
          closeComments={() => this.setState({ displayCommentsPopup: false })}
        />
        {this.state.displayAnswerPopup && (
          <AnswerPopupSkin
            displayAnswerPopup={this.state.displayAnswerPopup}
            continueCase={this.continueCaseHandler}
            simulationData={this.props.simulationData}
            currentQuestion={this.state.currentQuestion}
            gradeOutput={this.state.gradeOutput}
            caseEnded={this.state.caseEnded}
          />
        )}
        {EndOfPartPopupSkin && (
          <EndOfPartPopupSkin
            displayEndOfPartPopup={this.state.submitStep}
            confirmEndOfPart={this.confirmEndOfPart}
            cancelEndOfPart={this.cancelEndOfPart}
          />
        )}
        <ExitCasePopupSkin
          displayExitCasePrompt={this.state.displayExitCasePrompt}
          confirmExitCase={this.props.exitSimulation}
          cancelExitCase={() => this.setState({ displayExitCasePrompt: false })}
        />
        {CalculatorSkin && (
          <CalculatorSkin
            displayCalculator={this.state.displayCalculator}
            closeCalculator={this.closeCalculator}
          />
        )}
        {SettingsPanelSkin && (
          <SettingsPanelSkin
            displaySettings={this.state.displaySettings}
            closeSettings={this.closeSettings}
            settings={this.state.settings}
            updateSetting={this.updateSetting}
            resetSettings={this.resetSettings}
            simulationSkin={this.props.simulationSkin}
            simulationSkins={this.getSimulationSkinOptions()}
            changeSimulationSkin={this.props.changeSimulationSkin}
          />
        )}
        {FeedbackPopupSkin && (
          <FeedbackPopupSkin
            displayFeedback={this.state.displayFeedback}
            closeFeedback={this.closeFeedback}
            submitFeedback={this.submitFeedback}
          />
        )}
        {this.state.showHelpPopup && (
          <HelpPopupSkin
            closeHelpPopup={() => this.setState({ showHelpPopup: false })}
          />
        )}
        {this.state.displayCharacterWarning && (
          <CharacterWarningCDMQB
            closeMaxCharPopup={() =>
              this.setState({ displayCharacterWarning: false })
            }
          />
        )}
      </div>
    );
  }
}
